<template>
  <div :class="$style.container">
    <div :class="$style.content">

      <responsive-controller>
        <template #tablet>
          <header :class="$style.header">
            <nav
              :class="$style.navigation"
              @click="$router.go(-1)"
            >
              <base-icon name="arrows_arrow-left"></base-icon>
            </nav>
            <div
              :class="$style.title"
              v-text="$t('restore.password.title')"
            ></div>
          </header>
        </template>
        <template #default>
          <header :class="$style.header">
            <navigate-button
              v-absolute
              v-left="-80"
              variant="primary"
              @navigate="$router.go(-1)"
            ></navigate-button>
            <div
              :class="$style.title"
              v-text="$t('restore.password.title')"
            ></div>
          </header>
        </template>
      </responsive-controller>

      <section :class="$style.form">
        <labeled-input
          v-model="form.email"
          :label="getFieldLabel($v.form.email, 'registration.email.input.label')"
          :placeholder="$t('registration.email.input.placeholder')"
          :disabled="loading"
          :error="$v.form.email.$error"
          @focus="$v.form.email.$reset"
          @blur="$v.form.email.$touch"
        ></labeled-input>

        <div :class="$style.submit">
          <simple-button
            variant="secondary"
            :disabled="isInvalidForm"
            @click="handleSubmit"
          >
            {{ $t('restore.password.submit') }}
          </simple-button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import {
  email as emailValidator,
  required as requiredValidator
} from 'vuelidate/lib/validators';

import { apiCommon } from 'lib/utils/axios';

import restorePasswordRequest from 'api/requests/restore/restorePassword';

import { getLocaleCode } from '@/services/i18n-setup';
import { getInputFieldLabel } from 'services/helpers/errorHelpers';

import LabeledInput from 'molecules/common/LabeledInput';
import SimpleButton from 'molecules/common/SimpleButton';
import NavigateButton from 'molecules/common/NavigateButton';

export default {
  name: 'PasswordRestore',
  components: {
    LabeledInput,
    SimpleButton,
    NavigateButton,
  },
  head: {
    title: {
      inner: 'Восстановление доступа',
    },
    meta: [
      { name: 'description', content: 'Восстановление доступа. Принимай деньги с любыми целями. Делаешь сайт, продаёшь в Instagram, собираешь инвестиции или фрилансишь — монетизируй образ жизни вместе с Capusta.Space! Легко, молниеносно, безопасно.', id: 'desc' },
    ],
    script: [
      {
        inner: '(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({"gtm.start": new Date().getTime(),event:"gtm.js"});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!="dataLayer"?"&l="+l:"";j.async=true;j.src="https://www.googletagmanager.com/gtm.js?id="+i+dl;f.parentNode.insertBefore(j,f);})(window,document,"script","dataLayer","GTM-WKBLDC4");',
        type: 'text/javascript',
      },
    ],
    noscript: [
      {
        inner: '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-WKBLDC4" height="0" width="0" style="display:none;visibility:hidden"></iframe>',
      },
    ],
  },
  created() {
    if (this.$route.params.email) {
      this.form.email = this.$route.params.email;
    }
  },
  data() {
    return {
      form: {
        email: '',
      },
      loading: false,
    };
  },
  validations() {
    return {
      form: {
        email: {
          required: requiredValidator,
          email: emailValidator,
        },
      },
    };
  },
  computed: {
    isInvalidForm() {
      return this.$v.$invalid;
    },
  },
  methods: {
    getFieldLabel(field, label) {
      const fieldLabel = getInputFieldLabel(field, label);
      const fieldLabelTranslation = this.$t(fieldLabel);

      return fieldLabelTranslation;
    },
    async handleSubmit() {
      if (this.isInvalidForm) {
        this.$alert({
          type: 'warning',
          text: 'mistakes.in.form',
        });
        return;
      }

      this.loading = true;

      try {
        await restorePasswordRequest(apiCommon, {
          email: this.form.email,
          lang: getLocaleCode().toUpperCase(),
        });

        this.$alert({
          type: 'success',
          text: 'restore.password.request.success',
        });

        this.$router.push({ name: 'login' });
      } catch (error) {
        if (error.status === 400) {
          this.$alert({
            type: 'warning',
            text: 'restore.password.request.blocked',
          });
          return;
        }

        this.$alert({
          type: 'warning',
          text: 'restore.password.request.error',
        });

        this.loading = false;
      }
    },
  },
};
</script>

<style lang="sass" module>
  .container
    min-height: 100vh
    position: relative
    background: linear-gradient(249.76deg, #24A84D -8.32%, #068ACC 108.85%), rgba(255, 255, 255, 0.5)
    z-index: 1000

    +respond-to('small-medium')
      +flexbox
      +align-items(center)

    &:after
      content: ''
      width: 100%
      height: 100%
      position: absolute
      top: 0
      right: 0
      background-image: url('~images/illustrations/cat-question.svg')
      background-repeat: no-repeat
      background-position: bottom 8px right -63px
      z-index: -1

      +respond-to('medium')
        background-position: bottom 17px right 77px

  .header
    position: relative
    margin-bottom: 30px

    +respond-to('small-medium')
      margin-bottom: 50px

  .content
    width: 100%
    max-width: 460px
    margin: 0 auto

  .navigation
    +flexbox
    margin-bottom: 30px
    padding: 16px 20px
    cursor: pointer

  .title
    +font-type('semi-bold', 16, 20)
    color: var(--color-white)
    padding: 0 20px

    +respond-to('small-medium')
      +flexbox
      +justify-content(center)
      +align-items(center)
      +font-type('semi-bold', 20, 24)
      height: 60px
      padding: 0

  .form
    padding: 0 20px

    +respond-to('small-medium')
      padding: 0

  .submit
    width: 100%
    padding-top: 20px

    +respond-to('small-medium')
      max-width: 260px
      margin: 0 auto
      padding-top: 40px
</style>
